// Tron adapter.
// - HD derivation (m/44'/195'/0'/0/index) — pure & browser-safe (no tronweb):
//   a Tron address is the same keccak-derived 20 bytes as an EVM address,
//   prefixed with 0x41 and base58check-encoded.
// - TRC-20 balance reads use tronweb, lazily imported so it never lands in the
//   browser bundle.

import { HDKey } from '@scure/bip32';
import { mnemonicToSeedSync } from '@scure/bip39';
import { bytesToHex, encodeAbiParameters, hexToBytes, parseUnits, formatUnits } from 'viem';
import { privateKeyToAddress } from 'viem/accounts';
import { base58check } from '@scure/base';
import { sha256 } from '@noble/hashes/sha256';
import { TOKENS, type TokenSymbol, requireTokenInfo } from '@incpt/shared';
import type { DerivedAddresses, Address, IncomingTransfer } from '../types';

const PATH = (index: number) => `m/44'/195'/0'/0/${index}`;
const TRON_PREFIX = 0x41;
const b58check = base58check(sha256);

/** Convert a 20-byte EVM-style address (0x…) to a Tron base58 ('T…') address. */
export function evmToTron(ethAddress: string): string {
  const addr20 = hexToBytes(ethAddress as `0x${string}`);
  const payload = new Uint8Array(21);
  payload[0] = TRON_PREFIX;
  payload.set(addr20, 1);
  return b58check.encode(payload);
}

/** Convert a Tron base58 address to its EVM-style 20-byte hex representation. */
export function tronToEvm(address: string): `0x${string}` {
  const payload = b58check.decode(address);
  if (payload.length !== 21 || payload[0] !== TRON_PREFIX) throw new Error('Invalid Tron address');
  return bytesToHex(payload.slice(1));
}

/** Derive the Tron (base58 'T…') address + public key at the given index. */
export function deriveTronAddresses(seed: Uint8Array, index = 0): DerivedAddresses {
  const child = HDKey.fromMasterSeed(seed).derive(PATH(index));
  if (!child.privateKey || !child.publicKey) throw new Error('Tron derivation failed');
  const ethAddress = privateKeyToAddress(bytesToHex(child.privateKey));
  return {
    eoa: evmToTron(ethAddress),
    publicKey: bytesToHex(child.publicKey),
  };
}

// NB: tronweb requires `stateMutability` on every function entry (it calls
// stateMutability.toLowerCase()); the legacy `constant` flag alone crashes it.
const TRC20_BALANCE_ABI = [
  {
    constant: true,
    stateMutability: 'view',
    inputs: [{ name: 'who', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    type: 'function',
  },
];

const TRC20_TRANSFER_ABI = [
  {
    constant: false,
    stateMutability: 'nonpayable',
    inputs: [
      { name: '_to', type: 'address' },
      { name: '_value', type: 'uint256' },
    ],
    name: 'transfer',
    outputs: [{ name: '', type: 'bool' }],
    type: 'function',
  },
];

// approve(spender, value) + allowance(owner, spender) for the one-time approval
// the single-tx disperse path needs.
const TRC20_APPROVAL_ABI = [
  {
    constant: false,
    stateMutability: 'nonpayable',
    inputs: [
      { name: '_spender', type: 'address' },
      { name: '_value', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    type: 'function',
  },
  {
    constant: true,
    stateMutability: 'view',
    inputs: [
      { name: '_owner', type: 'address' },
      { name: '_spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    type: 'function',
  },
];

// Max uint256 — the one-time allowance granted to the Disperse contract. Safe
// because the contract only ever pulls from msg.sender (the approver themselves).
const MAX_UINT256 =
  '115792089237316195423570985008687907853269984665640564039457584007913129639935';

/** Convert a checksummed Tron base58 address to the 20-byte ABI address used by
 * contract parameters. base58check.decode validates the checksum for us. */
function tronToAbiAddress(address: string): `0x${string}` {
  const payload = b58check.decode(address);
  if (payload.length !== 21 || payload[0] !== TRON_PREFIX) {
    throw new Error('invalid_tron_address');
  }
  return bytesToHex(payload.slice(1));
}

type TronEnergyRequest = {
  ownerAddress: string;
  contractAddress: string;
  functionSelector: string;
  parameter: string;
};

type TronEnergyResponse = {
  result?: { result?: boolean; code?: string; message?: string };
  energy_required?: number;
  energy_used?: number;
};

// Per-call rejection ceilings, never rental targets. They are deliberately close
// to measured mainnet usage while leaving substantial room for Tron's dynamic
// energy factor:
//   approve (existing allowance slot) ~32k; FIRST approve (new slot) ~100k
//     measured mainnet triggerConstantContract 2026-07-23: 99_764 → max 150k
//   one fresh transfer ~130k -> max 180k
//   two-leg disperse ~212k -> max 250k
// A bad/hostile RPC response above the relevant ceiling fails closed before any
// refee order is placed. The previous 80k approve ceiling rejected every first
// USDT approve (~100k) as tron_energy_estimation_failed.
const MAX_APPROVE_ENERGY = 150_000;
const MAX_TRANSFER_ENERGY = 180_000;
const MAX_DISPERSE_ENERGY = 250_000;
// triggerConstantContract reports consumption at the confirmed-state snapshot,
// but unlike estimateEnergy Tron does not guarantee that value as a sufficient
// future fee limit. Keep a small bounded margin only on this official fallback;
// the primary estimateEnergy result is used verbatim.
const TRIGGER_CONSTANT_SAFETY_FACTOR = 1.05;

function validEnergy(value: unknown, maxEnergy: number): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(n)) return null;
  const energy = Math.ceil(n);
  if (!Number.isSafeInteger(energy) || energy <= 0 || energy > maxEnergy) return null;
  return energy;
}

/** Simulate the exact contract call that will be broadcast and return its energy
 * requirement without changing chain state.
 *
 * Official Tron API order:
 *   1. /wallet/estimateenergy (`energy_required`, guaranteed sufficient by Tron)
 *   2. /wallet/triggerconstantcontract (`energy_used`) when the node has the
 *      optional estimateEnergy endpoint disabled.
 *
 * Fail closed when neither simulation succeeds. Falling back to hard-coded
 * energy here would recreate the sponsor-loss / on-chain-revert failure mode this
 * preflight is designed to prevent.
 */
async function estimateTronContractEnergy(
  rpcUrl: string,
  request: TronEnergyRequest,
  maxEnergy: number,
  headers?: Record<string, string>,
): Promise<number> {
  const base = rpcUrl.replace(/\/$/, '');
  const body = JSON.stringify({
    owner_address: request.ownerAddress,
    contract_address: request.contractAddress,
    function_selector: request.functionSelector,
    parameter: request.parameter,
    visible: true,
  });
  const commonHeaders = { 'content-type': 'application/json', ...(headers ?? {}) };

  const call = async (
    path: '/wallet/estimateenergy' | '/wallet/triggerconstantcontract',
  ): Promise<TronEnergyResponse | null> => {
    try {
      const res = await fetch(`${base}${path}`, {
        method: 'POST',
        headers: commonHeaders,
        body,
        signal: AbortSignal.timeout(8_000),
      });
      if (!res.ok) return null;
      return (await res.json()) as TronEnergyResponse;
    } catch {
      return null;
    }
  };

  const exact = await call('/wallet/estimateenergy');
  if (exact?.result?.result === true) {
    const energy = validEnergy(exact.energy_required, maxEnergy);
    if (energy != null) return energy;
  }

  const simulated = await call('/wallet/triggerconstantcontract');
  if (simulated?.result?.result === true) {
    const used = validEnergy(simulated.energy_used, maxEnergy);
    if (used != null) {
      // Official fallback only: estimateEnergy is used verbatim (Tron documents it
      // as sufficient); triggerConstantContract is a confirmed-state snapshot.
      const withSnapshotMargin = validEnergy(used * TRIGGER_CONSTANT_SAFETY_FACTOR, maxEnergy);
      if (withSnapshotMargin != null) return withSnapshotMargin;
    }
  }

  throw new Error('tron_energy_estimation_failed');
}

/** Exact simulation of approve(spender, uint256.max). */
export function estimateTronApproveEnergy(
  rpcUrl: string,
  ownerAddress: string,
  token: TokenSymbol,
  spender: string,
  headers?: Record<string, string>,
): Promise<number> {
  const parameter = encodeAbiParameters(
    [{ type: 'address' }, { type: 'uint256' }],
    [tronToAbiAddress(spender), BigInt(MAX_UINT256)],
  ).slice(2);
  return estimateTronContractEnergy(
    rpcUrl,
    {
      ownerAddress,
      contractAddress: requireTokenInfo('trx', token).address,
      functionSelector: 'approve(address,uint256)',
      parameter,
    },
    MAX_APPROVE_ENERGY,
    headers,
  );
}

/** Exact simulation of TRC-20 transfer(to, amount) — used by the legacy
 * two-transaction path (principal + treasury fee as separate broadcasts). */
export function estimateTronTransferEnergy(
  rpcUrl: string,
  ownerAddress: string,
  token: TokenSymbol,
  to: string,
  amount: string,
  headers?: Record<string, string>,
): Promise<number> {
  const { address: tokenAddress, decimals } = requireTokenInfo('trx', token);
  const parameter = encodeAbiParameters(
    [{ type: 'address' }, { type: 'uint256' }],
    [tronToAbiAddress(to), parseUnits(amount, decimals)],
  ).slice(2);
  return estimateTronContractEnergy(
    rpcUrl,
    {
      ownerAddress,
      contractAddress: tokenAddress,
      functionSelector: 'transfer(address,uint256)',
      parameter,
    },
    MAX_TRANSFER_ENERGY,
    headers,
  );
}

/** Exact simulation of Disperse.disperseToken with the same sender, recipients,
 * and values the client will sign. Requires the allowance to already be on-chain;
 * callers must run this after the one-time approve is confirmed. */
export function estimateTronDisperseEnergy(
  rpcUrl: string,
  ownerAddress: string,
  token: TokenSymbol,
  contractAddress: string,
  recipients: string[],
  amounts: string[],
  headers?: Record<string, string>,
): Promise<number> {
  if (recipients.length === 0 || recipients.length !== amounts.length) {
    throw new Error('invalid_disperse_parameters');
  }
  const { address: tokenAddress, decimals } = requireTokenInfo('trx', token);
  const parameter = encodeAbiParameters(
    [{ type: 'address' }, { type: 'address[]' }, { type: 'uint256[]' }],
    [
      tronToAbiAddress(tokenAddress),
      recipients.map(tronToAbiAddress),
      amounts.map((amount) => parseUnits(amount, decimals)),
    ],
  ).slice(2);
  return estimateTronContractEnergy(
    rpcUrl,
    {
      ownerAddress,
      contractAddress,
      functionSelector: 'disperseToken(address,address[],uint256[])',
      parameter,
    },
    MAX_DISPERSE_ENERGY,
    headers,
  );
}

/** Derive the user's Tron private key (no 0x) from their mnemonic at `index`. */
function tronPrivateKey(mnemonic: string, index = 0): string {
  const seed = mnemonicToSeedSync(mnemonic);
  const child = HDKey.fromMasterSeed(seed).derive(PATH(index));
  if (!child.privateKey) throw new Error('Tron key derivation failed');
  return bytesToHex(child.privateKey).slice(2); // tronweb wants no 0x
}

export interface TronTransferParams {
  mnemonic: string;
  index?: number;
  /** tronweb fullHost — point at our /v1/rpc/trx proxy. */
  fullHost: string;
  /** Headers for fullHost requests (e.g. the API auth bearer). */
  headers?: Record<string, string>;
  token: TokenSymbol;
  to: string;
  /** Decimal token amount, e.g. "12.5". */
  amount: string;
}

/** Sign + broadcast a single TRC-20 transfer with the user's Tron key. Energy
 *  must already be delegated to the sender (refee). Returns the txid. */
/** Warm the lazily-imported tronweb bundle so the first Tron send doesn't pay its
 *  parse/eval cost on the critical path. Fire-and-forget (e.g. on Send-screen mount). */
export function preloadTronWeb(): Promise<unknown> {
  return import('tronweb');
}

export async function sendTronTransfer(p: TronTransferParams): Promise<string> {
  const { TronWeb } = await import('tronweb');
  const privateKey = tronPrivateKey(p.mnemonic, p.index ?? 0);

  const { address, decimals } = requireTokenInfo('trx', p.token);
  const tronWeb = new TronWeb({ fullHost: p.fullHost, privateKey, headers: p.headers });
  const contract = tronWeb.contract(TRC20_TRANSFER_ABI as never, address);
  const amountUnits = parseUnits(p.amount, decimals).toString();
  const txid = await contract
    .transfer(p.to, amountUnits)
    .send({ feeLimit: 100_000_000, shouldPollResponse: false });
  return String(txid);
}

/** The user's current allowance (base units) of `token` for `spender`. Used to
 *  decide whether the one-time approve for the disperse contract is still needed.
 *  `headers` lets the browser read through the authenticated /v1/rpc/trx proxy. */
export async function getTronAllowance(
  rpcUrl: string,
  token: TokenSymbol,
  owner: Address,
  spender: string,
  headers?: Record<string, string>,
): Promise<bigint> {
  const { TronWeb } = await import('tronweb');
  const tronWeb = new TronWeb({ fullHost: rpcUrl, headers });
  tronWeb.setAddress(owner); // a sender is required for constant calls
  const contract = tronWeb.contract(TRC20_APPROVAL_ABI as never, requireTokenInfo('trx', token).address);
  const a = await contract.allowance(owner, spender).call();
  return BigInt(a.toString());
}

/** The user's Tron base58 ('T…') address derived from their mnemonic at `index`.
 *  Used to read back the allowance after an approve (the disperse caller only
 *  holds the mnemonic). */
export function tronAddressFromMnemonic(mnemonic: string, index = 0): string {
  const ethAddress = privateKeyToAddress(`0x${tronPrivateKey(mnemonic, index)}`);
  return evmToTron(ethAddress);
}

/** Poll until `owner`'s allowance for `spender` covers the summed `amounts`, or
 *  throw on timeout. TRON needs the approve tx mined into a block before the
 *  disperse call can `transferFrom` — broadcasting disperse immediately after
 *  approve would revert (allowance still 0). */
export async function waitForTronAllowance(p: {
  rpcUrl: string;
  token: TokenSymbol;
  owner: string;
  spender: string;
  /** Decimal amounts the allowance must cover (e.g. ["12.5", "1.5"]). */
  amounts: string[];
  headers?: Record<string, string>;
  timeoutMs?: number;
  intervalMs?: number;
}): Promise<void> {
  const { decimals } = requireTokenInfo('trx', p.token);
  const min = p.amounts.reduce((s, a) => s + parseUnits(a, decimals), 0n);
  const timeoutMs = p.timeoutMs ?? 45_000;
  // Tron blocks are ~3s; poll a bit faster than block time so we detect the mined
  // allowance close to when it lands instead of waiting up to a full extra cycle.
  const intervalMs = p.intervalMs ?? 1_200;
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const a = await getTronAllowance(p.rpcUrl, p.token, p.owner, p.spender, p.headers).catch(
      () => 0n,
    );
    if (a >= min) return;
    if (Date.now() >= deadline) throw new Error('approval_not_confirmed');
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

export interface TronApproveParams {
  mnemonic: string;
  index?: number;
  fullHost: string;
  headers?: Record<string, string>;
  token: TokenSymbol;
  /** Disperse contract to approve (max allowance). */
  spender: string;
}

/** One-time `approve(spender, maxUint)` so the disperse contract can pull the
 *  user's tokens. Energy must already be delegated (refee). Returns the txid. */
export async function sendTronApprove(p: TronApproveParams): Promise<string> {
  const { TronWeb } = await import('tronweb');
  const privateKey = tronPrivateKey(p.mnemonic, p.index ?? 0);
  const tronWeb = new TronWeb({ fullHost: p.fullHost, privateKey, headers: p.headers });
  const contract = tronWeb.contract(TRC20_APPROVAL_ABI as never, requireTokenInfo('trx', p.token).address);
  const txid = await contract
    .approve(p.spender, MAX_UINT256)
    .send({ feeLimit: 100_000_000, shouldPollResponse: false });
  return String(txid);
}

export interface TronDisperseParams {
  mnemonic: string;
  index?: number;
  fullHost: string;
  headers?: Record<string, string>;
  token: TokenSymbol;
  /** Disperse contract address. */
  contract: string;
  /** Recipients in order (e.g. [recipient, treasury]). */
  recipients: string[];
  /** Decimal token amounts matching `recipients` (e.g. ["12.5", "1.5"]). */
  amounts: string[];
}

export interface SignedTronTransaction {
  txID: string;
  raw_data: unknown;
  raw_data_hex: string;
  signature: string[];
  visible?: boolean;
}

/** Build and sign the atomic disperse transaction WITHOUT broadcasting it.
 *  The API durably stores this signed payload before broadcasting, closing the
 *  crash window where tokens moved but no server-side txid existed. */
export async function signTronDisperse(
  p: TronDisperseParams,
): Promise<{ txHash: string; signedTransaction: SignedTronTransaction }> {
  const { TronWeb } = await import('tronweb');
  const privateKey = tronPrivateKey(p.mnemonic, p.index ?? 0);
  const { address: tokenAddr, decimals } = requireTokenInfo('trx', p.token);
  const tronWeb = new TronWeb({ fullHost: p.fullHost, privateKey, headers: p.headers });
  const values = p.amounts.map((a) => parseUnits(a, decimals).toString());
  const owner = tronWeb.address.fromPrivateKey(privateKey);
  if (!owner) throw new Error('tron_owner_unavailable');
  const built = await tronWeb.transactionBuilder.triggerSmartContract(
    p.contract,
    'disperseToken(address,address[],uint256[])',
    { feeLimit: 200_000_000, callValue: 0 },
    [
      { type: 'address', value: tokenAddr },
      { type: 'address[]', value: p.recipients },
      { type: 'uint256[]', value: values },
    ],
    tronWeb.address.toHex(owner),
  );
  if (!built.result?.result || !built.transaction?.txID) {
    throw new Error(`tron_disperse_build_failed:${JSON.stringify(built.result ?? {})}`);
  }
  // Give the durable outbox enough time to rebroadcast after an API/process
  // restart. TronWeb's default expiration is too short for a one-minute sweeper.
  // txLocal: rebuild txID locally — default path calls wallet/getsignweight via
  // our allowlisted /v1/rpc/trx proxy and fails with "Error generating a new
  // transaction id." when that path is blocked.
  const durableTransaction = await tronWeb.transactionBuilder.extendExpiration(
    built.transaction,
    10 * 60,
    { txLocal: true },
  );
  const signed = await tronWeb.trx.sign(durableTransaction, privateKey);
  if (
    typeof signed === 'string' ||
    !signed.txID ||
    !Array.isArray(signed.signature) ||
    signed.signature.length === 0
  ) {
    throw new Error('tron_disperse_sign_failed');
  }
  return {
    txHash: signed.txID,
    signedTransaction: signed as SignedTronTransaction,
  };
}

/** Raw on-chain balance (token base units, 6 decimals for USDT/USDC). */
export async function getTronBalance(
  rpcUrl: string,
  address: Address,
  token: TokenSymbol,
): Promise<bigint> {
  const { TronWeb } = await import('tronweb');
  const tronWeb = new TronWeb({ fullHost: rpcUrl });
  tronWeb.setAddress(address); // a sender is required for constant calls
  const contract = tronWeb.contract(TRC20_BALANCE_ABI as never, requireTokenInfo('trx', token).address);
  const bal = await contract.balanceOf(address).call();
  return BigInt(bal.toString());
}

/**
 * Whether `address` currently holds a positive `token` balance, read from the
 * TronGrid indexer (`/v1/accounts/{address}`). This decides the send's energy
 * sizing: a recipient that ALREADY holds USDT reuses its storage slot (~65k
 * energy), while a fresh one pays the ~2× "new storage slot" penalty (~130k).
 *
 * We read it here (not via Alchemy `balanceOf`) because Alchemy's Tron endpoint
 * only proxies the core node API — a constant-contract `balanceOf` there is
 * flaky, and any failure silently returned "no balance", which forced the
 * expensive fresh-recipient energy (≈2× the cost) on EVERY send. TronGrid is the
 * same reliable, key-authenticated indexer we already use for deposits.
 *
 * Throws on transport/HTTP error so the caller can fall back (to Alchemy, then
 * to the safe "fresh" assumption) rather than mis-size on a transient blip.
 */
export async function tronHoldsToken(
  baseUrl: string,
  address: Address,
  token: TokenSymbol,
  apiKey?: string,
): Promise<boolean> {
  const url = `${baseUrl.replace(/\/$/, '')}/v1/accounts/${address}`;
  const headers = apiKey ? { 'TRON-PRO-API-KEY': apiKey } : undefined;

  let res: Response | undefined;
  for (let attempt = 0; attempt < 3; attempt++) {
    res = await fetch(url, { headers });
    if (res.status !== 429) break;
    await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
  }
  if (!res || !res.ok) throw new Error(`trongrid account HTTP ${res?.status ?? 'no_response'}`);
  const json = (await res.json()) as {
    data?: Array<{ trc20?: Array<Record<string, string>> }>;
  };

  const contractAddr = requireTokenInfo('trx', token).address;
  const trc20 = json.data?.[0]?.trc20 ?? [];
  for (const entry of trc20) {
    const raw = entry?.[contractAddr];
    if (raw === undefined) continue;
    // TronGrid returns integer strings; guard against a malformed value so one
    // bad entry can't throw and abort the whole holder check.
    try {
      if (BigInt(raw) > 0n) return true;
    } catch {
      /* not a parseable amount — treat as no balance for this entry */
    }
  }
  return false;
}

/**
 * Whether a TRON account is activated (registered on-chain). A fresh address
 * that only ever received TRC-20 — never TRX — is NOT registered and can't sign
 * (TRON rejects its txs with "account does not exist"). `/wallet/getaccount`
 * returns `{}` for such an address. Read errors are unknown, not proof of
 * activation: callers make sponsor-money and persistent-state decisions from
 * this result, so they must fail closed instead of poisoning `tronActivated`.
 */
export async function getTronAccountExists(rpcUrl: string, address: Address): Promise<boolean> {
  const res = await fetch(`${rpcUrl.replace(/\/$/, '')}/wallet/getaccount`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ address, visible: true }),
    signal: AbortSignal.timeout(8_000),
  });
  if (!res.ok) throw new Error(`tron_account_http_${res.status}`);
  const json = (await res.json()) as Record<string, unknown>;
  if (!json || typeof json !== 'object') throw new Error('tron_account_malformed');
  return Object.keys(json).length > 0;
}

export type TronAccountResources = {
  energy: number;
  /** Delegated/staked energy ceiling and how much of it is already spent. A
   *  caller that just triggered a burn can tell a snapshot taken before that
   *  burn from one taken after it, which `energy` alone cannot express. */
  energyLimit: number;
  energyUsed: number;
  /** Usable bandwidth for one tx: max(free, staked/delegated). TRON does not
   *  let a single broadcast draw from both buckets at once. */
  bandwidth: number;
  freeBandwidth: number;
  stakedBandwidth: number;
};

/** Energy + bandwidth currently available to a TRON account (already on-chain:
 *  staked or delegated, minus what's used). Lets the caller rent only the
 *  *deficit* from refee instead of blindly re-renting the full amount on every
 *  send — that blind renting stacked hundreds of k of unused energy and was
 *  billed each time. `/wallet/getaccountresource` is a core node method, so it's
 *  served by the Alchemy proxy. On any error returns zeroes, so the caller falls
 *  back to renting the full amount (i.e. today's behaviour) — never less safe. */
export async function getTronResources(
  rpcUrl: string,
  address: Address,
): Promise<TronAccountResources> {
  try {
    return await getTronResourcesStrict(rpcUrl, address);
  } catch {
    return {
      energy: 0,
      energyLimit: 0,
      energyUsed: 0,
      bandwidth: 0,
      freeBandwidth: 0,
      stakedBandwidth: 0,
    };
  }
}

/** Strict variant for sponsor-money decisions. An RPC failure is unknown, not
 * proof that the account has zero resources; callers must abort rather than buy
 * a duplicate full rental from a fabricated zero snapshot. */
export async function getTronResourcesStrict(
  rpcUrl: string,
  address: Address,
): Promise<TronAccountResources> {
  const res = await fetch(`${rpcUrl.replace(/\/$/, '')}/wallet/getaccountresource`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ address, visible: true }),
    signal: AbortSignal.timeout(8_000),
  });
  if (!res.ok) throw new Error(`tron_resources_http_${res.status}`);
  const j = (await res.json()) as Record<string, number | undefined>;
  if (!j || Object.keys(j).length === 0) throw new Error('tron_resources_empty');
  const required = [
    'EnergyLimit',
    'EnergyUsed',
    'freeNetLimit',
    'freeNetUsed',
    'NetLimit',
    'NetUsed',
  ];
  if (!required.some((key) => Object.prototype.hasOwnProperty.call(j, key))) {
    throw new Error('tron_resources_malformed');
  }
  if (required.some((key) => j[key] !== undefined && !Number.isFinite(j[key]))) {
    throw new Error('tron_resources_malformed');
  }
  const energyLimit = j.EnergyLimit ?? 0;
  const energyUsed = j.EnergyUsed ?? 0;
  const energy = Math.max(0, energyLimit - energyUsed);
  // Free daily Net and staked/delegated Net are separate budgets. A tx must be
  // covered entirely by one of them (or by burning TRX — our wallets hold 0).
  // Summing them caused BANDWITH_ERROR when free+staked looked "enough" but
  // neither bucket alone covered the disperse size (~800).
  const freeBandwidth = Math.max(0, (j.freeNetLimit ?? 0) - (j.freeNetUsed ?? 0));
  const stakedBandwidth = Math.max(0, (j.NetLimit ?? 0) - (j.NetUsed ?? 0));
  return {
    energy,
    energyLimit,
    energyUsed,
    freeBandwidth,
    stakedBandwidth,
    bandwidth: Math.max(freeBandwidth, stakedBandwidth),
  };
}

/**
 * Cheap liveness probe for the Tron RPC path — the same node the client will
 * broadcast through. Lets callers avoid spending on energy rental when the RPC is
 * down and a broadcast/confirm could never succeed anyway. Returns false on any
 * error. `/wallet/getnowblock` is a core node method served by the Alchemy proxy.
 */
export async function isTronRpcHealthy(
  rpcUrl: string,
  headers?: Record<string, string>,
): Promise<boolean> {
  try {
    const res = await fetch(`${rpcUrl.replace(/\/$/, '')}/wallet/getnowblock`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(headers ?? {}) },
      body: '{}',
    });
    if (!res.ok) return false;
    const j = (await res.json().catch(() => null)) as { blockID?: string } | null;
    return !!j?.blockID;
  } catch {
    return false;
  }
}

/**
 * On-chain execution result of a broadcast TRON transaction, by tx id.
 *
 * CRITICAL for money safety: a TRON contract call (a TRC-20 transfer, or our
 * disperse call) can be broadcast and INCLUDED IN A BLOCK yet still FAIL to
 * execute — most commonly "Out of Energy" — in which case NO tokens move even
 * though the tx is "confirmed" on explorers. `receipt.result` distinguishes a
 * true transfer ('SUCCESS') from a reverted one ('OUT_OF_ENERGY', 'REVERT', …).
 *
 * Returns:
 *   'success' — mined and executed successfully (tokens moved),
 *   'failed'  — mined but reverted / rejected (tokens did NOT move),
 *   'unknown' — not yet mined, or the node/RPC couldn't be read (caller decides).
 *
 * `/wallet/gettransactioninfobyid` is a core node method served by the Alchemy proxy.
 */
export async function getTronTxResult(
  rpcUrl: string,
  txHash: string,
  headers?: Record<string, string>,
): Promise<'success' | 'failed' | 'unknown'> {
  try {
    const res = await fetch(`${rpcUrl.replace(/\/$/, '')}/wallet/gettransactioninfobyid`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(headers ?? {}) },
      body: JSON.stringify({ value: txHash }),
    });
    if (!res.ok) return 'unknown';
    const j = (await res.json()) as {
      id?: string;
      result?: string; // top-level 'FAILED' for a rejected tx
      receipt?: { result?: string };
    };
    // Empty object `{}` → the node hasn't indexed the tx yet (not mined).
    if (!j || !j.id) return 'unknown';
    const receiptResult = j.receipt?.result;
    // Contract calls (TRC-20 transfer / disperse) carry receipt.result. 'SUCCESS'
    // is the only value that means the tokens actually moved.
    if (receiptResult) return receiptResult === 'SUCCESS' ? 'success' : 'failed';
    // Explicit top-level failure (rejected before execution).
    if (j.result === 'FAILED') return 'failed';
    // A mined tx with a receipt but no contract result (e.g. a plain TRX send).
    return 'success';
  } catch {
    return 'unknown';
  }
}

const TRANSFER_EVENT_TOPIC = 'ddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';

function tronLogAddress(value: string): string | null {
  if (value.startsWith('T')) return tronToAbiAddress(value).slice(2).toLowerCase();
  const hex = value.toLowerCase().replace(/^0x/, '');
  // FullNode responses may use either 20-byte ABI addresses or 21-byte Tron
  // addresses prefixed with 0x41. Topics are always 32-byte padded.
  if (hex.length === 42 && hex.startsWith('41')) return hex.slice(2);
  if (hex.length >= 40 && /^[0-9a-f]+$/.test(hex)) return hex.slice(-40);
  return null;
}

/** Verify that a successful Tron transaction emitted the exact TRC-20 Transfer
 * expected by our server-authored transfer row. A successful unrelated txHash is
 * not payment proof. Returns null while the tx is unavailable. */
export async function verifyTronTokenTransfer(
  rpcUrl: string,
  txHash: string,
  expected: {
    token: TokenSymbol;
    from: string;
    to: string;
    amount: string;
  },
  headers?: Record<string, string>,
): Promise<boolean | null> {
  try {
    const res = await fetch(`${rpcUrl.replace(/\/$/, '')}/wallet/gettransactioninfobyid`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(headers ?? {}) },
      body: JSON.stringify({ value: txHash }),
      signal: AbortSignal.timeout(8_000),
    });
    if (!res.ok) return null;
    const info = (await res.json()) as {
      id?: string;
      receipt?: { result?: string };
      log?: Array<{ address?: string; topics?: string[]; data?: string }>;
    };
    if (!info?.id) return null;
    if (info.receipt?.result !== 'SUCCESS') return false;

    const token = tronLogAddress(requireTokenInfo('trx', expected.token).address);
    const from = tronLogAddress(expected.from);
    const to = tronLogAddress(expected.to);
    const amount = parseUnits(expected.amount, requireTokenInfo('trx', expected.token).decimals);
    if (!token || !from || !to) return false;

    return (info.log ?? []).some((log) => {
      const topics = log.topics ?? [];
      if (
        tronLogAddress(log.address ?? '') !== token ||
        topics[0]?.toLowerCase().replace(/^0x/, '') !== TRANSFER_EVENT_TOPIC ||
        tronLogAddress(topics[1] ?? '') !== from ||
        tronLogAddress(topics[2] ?? '') !== to
      ) {
        return false;
      }
      try {
        return BigInt(`0x${(log.data ?? '').replace(/^0x/, '') || '0'}`) === amount;
      } catch {
        return false;
      }
    });
  } catch {
    return null;
  }
}

// Contract address → {symbol, decimals} for mapping TronGrid TRC20 results.
const TRX_TOKEN_BY_ADDR = new Map(
  (Object.entries(TOKENS.trx) as [TokenSymbol, { address: string; decimals: number }][]).map(
    ([sym, info]) => [info.address, { sym, decimals: info.decimals }],
  ),
);

interface TronGridTrc20Tx {
  transaction_id: string;
  token_info?: { address?: string; decimals?: number; symbol?: string };
  block_timestamp: number;
  from: string;
  to: string;
  type?: string;
  value: string;
}

async function fetchTrc20Page(
  baseUrl: string,
  address: Address,
  apiKey: string | undefined,
  limit: number,
  contractAddress: string,
): Promise<TronGridTrc20Tx[]> {
  const url =
    `${baseUrl.replace(/\/$/, '')}/v1/accounts/${address}/transactions/trc20` +
    `?only_to=true&only_confirmed=true&limit=${limit}&contract_address=${contractAddress}`;
  const headers = apiKey ? { 'TRON-PRO-API-KEY': apiKey } : undefined;

  // The public TronGrid endpoint has a low per-IP rate limit and returns 429
  // under bursts (multiple addresses synced per history load). Retry with a
  // short backoff; set TRONGRID_API_KEY to lift the limit in production.
  let res: Response | undefined;
  for (let attempt = 0; attempt < 4; attempt++) {
    res = await fetch(url, { headers, signal: AbortSignal.timeout(8_000) });
    if (res.status !== 429) break;
    await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
  }
  if (!res || !res.ok) throw new Error(`trongrid trc20 HTTP ${res?.status ?? 'no_response'}`);
  const json = (await res.json()) as { data?: TronGridTrc20Tx[]; success?: boolean };
  return json.data ?? [];
}

/**
 * Incoming USDT/USDC transfers to `address`, via the TronGrid HTTP v1 indexer
 * (`/v1/accounts/{address}/transactions/trc20`). Alchemy's Tron endpoint only
 * proxies the core node API and has no transfer-history method, so TronGrid (or
 * another indexer) is required here. `baseUrl` defaults to the public endpoint;
 * pass an `apiKey` for higher rate limits. Newest first; throws on HTTP error.
 *
 * Queried per known contract. A single unfiltered page is only the latest N
 * TRC-20 transfers of ANY token, so spam pushes real USDT/USDC off the page
 * and they never get a history row.
 */
export async function getIncomingTrc20Transfers(
  baseUrl: string,
  address: Address,
  apiKey?: string,
  limit = 200,
): Promise<IncomingTransfer[]> {
  const contracts = [...TRX_TOKEN_BY_ADDR.keys()];
  const pages: TronGridTrc20Tx[] = [];
  let lastError: Error | undefined;
  for (const contract of contracts) {
    try {
      pages.push(...(await fetchTrc20Page(baseUrl, address, apiKey, limit, contract)));
    } catch (e) {
      lastError = e instanceof Error ? e : new Error(String(e));
    }
  }
  if (pages.length === 0 && lastError) throw lastError;

  const out: IncomingTransfer[] = [];
  // Count how many times (txid, token) appears so that a disperse-style contract
  // call (multiple internal TRC-20 transfers in one tx) gets a unique idempotency
  // key per occurrence. The first occurrence uses the plain key (backward-compat
  // with already-stored rows); subsequent ones get a `:N` suffix.
  const txTokenCount = new Map<string, number>();
  for (const tx of pages) {
    const meta = TRX_TOKEN_BY_ADDR.get(tx.token_info?.address ?? '');
    if (!meta || tx.to !== address) continue; // only our tokens, only inbound
    const base = `${tx.transaction_id}:${tx.token_info?.address}`;
    const n = txTokenCount.get(base) ?? 0;
    txTokenCount.set(base, n + 1);
    out.push({
      chain: 'trx',
      token: meta.sym,
      txHash: tx.transaction_id,
      uniqueId: n === 0 ? base : `${base}:${n}`,
      from: tx.from,
      to: tx.to,
      amount: formatUnits(BigInt(tx.value), meta.decimals),
      timestamp: tx.block_timestamp,
    });
  }
  return out;
}

/** Native TRX balance in sun (1 TRX = 1e6 sun). Unactivated accounts → 0. */
export async function getTronNativeBalance(rpcUrl: string, address: Address): Promise<bigint> {
  const res = await fetch(`${rpcUrl.replace(/\/$/, '')}/wallet/getaccount`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ address, visible: true }),
    signal: AbortSignal.timeout(8_000),
  });
  if (!res.ok) throw new Error(`tron_native_balance_http_${res.status}`);
  const json = (await res.json()) as { balance?: number | string };
  if (json?.balance === undefined || json?.balance === null) return 0n;
  return BigInt(json.balance);
}

export interface TronNativeTransferParams {
  mnemonic: string;
  index?: number;
  fullHost: string;
  headers?: Record<string, string>;
  to: string;
  /** Amount in sun (1 TRX = 1e6). */
  amountSun: bigint;
}

/** Sign + broadcast a native TRX TransferContract. User pays bandwidth (free
 *  quota or TRX burn) — no energy sponsorship. */
export async function sendTronNative(p: TronNativeTransferParams): Promise<string> {
  if (p.amountSun <= 0n || p.amountSun > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error('invalid_amount');
  }
  const { TronWeb } = await import('tronweb');
  const privateKey = tronPrivateKey(p.mnemonic, p.index ?? 0);
  const tronWeb = new TronWeb({ fullHost: p.fullHost, privateKey, headers: p.headers });
  const from = tronWeb.address.fromPrivateKey(privateKey);
  if (!from) throw new Error('tron_key_invalid');

  // Amount-only preflight. Bandwidth burn is enforced by the node; the server
  // fee-quote already reserved headroom when quoting a non-zero fee.
  const balance = BigInt((await tronWeb.trx.getBalance(from)) || 0);
  if (balance < p.amountSun) throw new Error('insufficient_native_gas');

  const result = await tronWeb.trx.sendTransaction(p.to, Number(p.amountSun));
  if (!result?.result && !result?.txid) {
    const msg = String(result?.message ?? result?.code ?? 'broadcast_failed');
    if (/balance|resource|bandwidth/i.test(msg)) throw new Error('insufficient_native_gas');
    throw new Error(`transfer_failed:${msg.slice(0, 120)}`);
  }
  return String(result.txid ?? result.transaction?.txID ?? '');
}

/** Normalize Tron address from hex (41…) or base58 to lowercase 20-byte hex. */
function tronAddrKey(addr: string): string {
  const a = addr.trim();
  if (a.startsWith('T')) return tronToEvm(a).slice(2).toLowerCase();
  const hex = a.toLowerCase().replace(/^0x/, '');
  if (hex.length === 42 && hex.startsWith('41')) return hex.slice(2);
  if (hex.length === 40) return hex;
  throw new Error('invalid_tron_address');
}

function toTronBase58(addr: string): string {
  return addr.startsWith('T') ? addr : evmToTron(`0x${tronAddrKey(addr)}`);
}

/** Verify a mined native TRX TransferContract matches expected from/to/amount. */
export async function verifyTronNativeTransfer(
  rpcUrl: string,
  txHash: string,
  expected: { from: string; to: string; amountSun: bigint },
  headers?: Record<string, string>,
): Promise<boolean | null> {
  try {
    const res = await fetch(`${rpcUrl.replace(/\/$/, '')}/wallet/gettransactionbyid`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(headers ?? {}) },
      body: JSON.stringify({ value: txHash }),
    });
    if (!res.ok) return null;
    const tx = (await res.json()) as {
      txID?: string;
      raw_data?: {
        contract?: Array<{
          type?: string;
          parameter?: {
            value?: { amount?: number | string; owner_address?: string; to_address?: string };
          };
        }>;
      };
    };
    if (!tx?.txID) return null;
    const v = tx.raw_data?.contract?.[0];
    if (!v || v.type !== 'TransferContract') return false;
    const p = v.parameter?.value;
    if (!p?.owner_address || !p?.to_address || p.amount === undefined) return false;
    return (
      tronAddrKey(p.owner_address) === tronAddrKey(expected.from) &&
      tronAddrKey(p.to_address) === tronAddrKey(expected.to) &&
      BigInt(p.amount) === expected.amountSun
    );
  } catch {
    return null;
  }
}

/**
 * Incoming native TRX transfers via TronGrid
 * (`/v1/accounts/{address}/transactions`). Filters TransferContract only.
 */
export async function getIncomingTrxTransfers(
  baseUrl: string,
  address: Address,
  apiKey?: string,
  limit = 25,
): Promise<IncomingTransfer[]> {
  const url = `${baseUrl.replace(/\/$/, '')}/v1/accounts/${address}/transactions?only_to=true&only_confirmed=true&limit=${limit}`;
  const headers = apiKey ? { 'TRON-PRO-API-KEY': apiKey } : undefined;

  let res: Response | undefined;
  for (let attempt = 0; attempt < 4; attempt++) {
    res = await fetch(url, { headers, signal: AbortSignal.timeout(8_000) });
    if (res.status !== 429) break;
    await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
  }
  if (!res || !res.ok) throw new Error(`trongrid trx HTTP ${res?.status ?? 'no_response'}`);
  const json = (await res.json()) as {
    data?: Array<{
      txID: string;
      block_timestamp: number;
      raw_data?: {
        contract?: Array<{
          type?: string;
          parameter?: {
            value?: { amount?: number | string; owner_address?: string; to_address?: string };
          };
        }>;
        timestamp?: number;
      };
    }>;
  };

  const toKey = tronAddrKey(address);
  const out: IncomingTransfer[] = [];
  for (const tx of json.data ?? []) {
    const c = tx.raw_data?.contract?.[0];
    if (c?.type !== 'TransferContract') continue;
    const v = c.parameter?.value;
    if (!v?.to_address || v.amount === undefined) continue;
    try {
      if (tronAddrKey(v.to_address) !== toKey) continue;
      const amountSun = BigInt(v.amount);
      if (amountSun <= 0n) continue;
      out.push({
        chain: 'trx',
        token: 'TRX',
        txHash: tx.txID,
        uniqueId: `${tx.txID}:trx`,
        from: v.owner_address ? toTronBase58(v.owner_address) : '',
        to: toTronBase58(v.to_address),
        amount: formatUnits(amountSun, 6),
        timestamp: tx.block_timestamp || tx.raw_data?.timestamp || Date.now(),
      });
    } catch {
      /* skip malformed row */
    }
  }
  return out;
}

